import fuzzysort from "fuzzysort";
import { ref, type Ref } from "vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

export interface Command {
  id: string;
  label: string;
  group: string;
  /**
   * Primary match target, replacing `label` when set. Use it when the label carries decoration
   * that is identical across many commands — indexing that would match all of them at once.
   */
  searchText?: string;
  /** Secondary match targets, scored independently of the primary one and never displayed. */
  keywords?: string[];
  icon?: string;
  action: () => unknown | Promise<unknown>;
}

interface CommandGroup {
  label: string;
  commands: Command[];
}

const RECENT_KEY = "command-palette-recent";
const MAX_RECENT = 5;
// fuzzysort scores 0..1 (1 = exact). Below ~0.5 a target only matches as scattered single
// characters, which across thousands of company names means almost all of them.
const MATCH_THRESHOLD = 0.5;
const MAX_RESULTS = 20;

// Singleton state
const isOpen = ref(false);
const commands: Ref<Command[]> = ref([]);

function getRecentIds(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function addToRecent(commandId: string) {
  const recent = getRecentIds();
  const updated = [commandId, ...recent.filter((id) => id !== commandId)].slice(
    0,
    MAX_RECENT
  );
  localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
}

export function useCommandPalette() {
  const { globalErrorHandler } = useGlobalHandlers();

  function close() {
    isOpen.value = false;
  }

  function toggle() {
    isOpen.value = !isOpen.value;
  }

  function registerCommands(newCommands: Command[]) {
    commands.value = newCommands;
  }

  function searchCommands(query: string): CommandGroup[] {
    const available = commands.value;
    let matched: Command[];

    if (!query) {
      // Empty query: show recent commands first, then all available
      const recentIds = getRecentIds();
      const recentCommands = recentIds
        .map((id) => available.find((c) => c.id === id))
        .filter((c): c is Command => c !== undefined);

      if (recentCommands.length > 0) {
        const nonRecent = available.filter((c) => !recentIds.includes(c.id));
        const groups: CommandGroup[] = [];

        groups.push({ label: "Recent", commands: recentCommands });

        const grouped = groupCommands(nonRecent);
        groups.push(...grouped);
        return groups;
      }

      matched = available;
    } else {
      // Each key is scored independently and the best wins, so a strong company-name hit is not
      // diluted by the email and registry code sitting alongside it in keywords.
      const results = fuzzysort.go(query, available, {
        keys: [
          (command: Command) => command.searchText ?? command.label,
          // Array-valued keys have to be flattened by hand; fuzzysort scores a plain
          // "keywords" key as 0, which silently disables keyword search entirely.
          (command: Command) => command.keywords?.join(" ") ?? "",
        ],
        threshold: MATCH_THRESHOLD,
        limit: MAX_RESULTS,
      });
      matched = results.map((r) => r.obj);
    }

    return groupCommands(matched);
  }

  async function executeCommand(command: Command) {
    close();
    try {
      await Promise.resolve(command.action());
      addToRecent(command.id);
    } catch (error) {
      globalErrorHandler(error);
    }
  }

  return {
    isOpen,
    close,
    toggle,
    registerCommands,
    searchCommands,
    executeCommand,
  };
}

function groupCommands(cmds: Command[]): CommandGroup[] {
  const groupMap = new Map<string, Command[]>();
  const groupOrder = ["Navigation", "Admin", "Actions", "Accounts"];

  for (const cmd of cmds) {
    const existing = groupMap.get(cmd.group);
    if (existing) {
      existing.push(cmd);
    } else {
      groupMap.set(cmd.group, [cmd]);
    }
  }

  const groups: CommandGroup[] = [];
  // Add groups in preferred order
  for (const label of groupOrder) {
    const commands = groupMap.get(label);
    if (commands) {
      groups.push({ label, commands });
      groupMap.delete(label);
    }
  }
  // Add any remaining groups
  for (const [label, commands] of groupMap) {
    groups.push({ label, commands });
  }

  return groups;
}
